import { useQueryClient } from '@tanstack/react-query';
import type { EventSubscription } from 'expo-notifications';
import {
  addNotificationResponseReceivedListener,
  registerTaskAsync,
  setBadgeCountAsync
} from 'expo-notifications';
import { defineTask } from 'expo-task-manager';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useRegisterMobilePushEndpoint } from '@/features/api/hooks/shared-push/useRegisterMobilePushEndpoint';
import { useUnregisterMobilePushEndpoint } from '@/features/api/hooks/shared-push/useUnregisterMobilePushEndpoint';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { useDebugStore } from '@/features/experiments/store/useDebugStore';
import { PUSH_PLATFORM } from '@/features/notifications/constants/platform';
import { useRefetchOwnChats } from '@/features/notifications/hooks/useRefetchOwnChats';
import { useNotificationStore } from '@/features/notifications/store/useNotificationStore';
import type { BackgroundNotification } from '@/features/notifications/types/backgroundNotification';
import type { NotificationData } from '@/features/notifications/types/notificationData';
import {
  getRelatedEntityId,
  getBackgroundNotificationData
} from '@/features/notifications/utils/notificationData';
import { TabRoute } from '@/navigation/types/navigation';
import { useBadgeStore } from '@/shared/store/useBadgeStore';
import { isWeb } from '@/shared/utils/platform';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const BACKGROUND_NOTIFICATION_TASK = 'BACKGROUND-NOTIFICATION-TASK';

export const NotificationConfiguration: FC = () => {
  const theme = useAppTheme();
  const { userId } = useAppState();
  const { isAuthenticated } = useAuth();

  const registerMobilePush = useRegisterMobilePushEndpoint();
  const unRegisterMobilePush = useUnregisterMobilePushEndpoint();

  const responseListener = useRef<EventSubscription>(null);

  const pushToken = useNotificationStore((state) => state.token);
  const updateToken = useNotificationStore((state) => state.updateToken);

  const [personId, setPersonId] = useState<string | undefined>();

  const queryClient = useQueryClient();
  const { refetchOwnChats } = useRefetchOwnChats();

  const updateBadge = useBadgeStore((state) => state.updateBadge);
  const updateNewPostBadgeEntriesChanged = useBadgeStore(
    (state) => state.updateNewPostBadgeEntriesChanged
  );

  const updateDebugMessage = useDebugStore((state) => state.updateDebugMessage);

  const handleNotification = useCallback(
    async (data?: NotificationData) => {
      try {
        if (!data) {
          console.warn('Notification data is empty');
          return;
        }

        switch (data.eventName) {
          case 'ClientPostCreateConfirmation': {
            const postId = getRelatedEntityId('postId', data);
            if (!postId) return;
            queryClient.invalidateQueries({
              queryKey: [API_CACHE_KEYS.POST, { postId }]
            });
            updateBadge(TabRoute.Radio, ' ', theme.colors.error);
            updateNewPostBadgeEntriesChanged(true);
            return;
          }
          case 'ClientPostChangeConfirmation':
          case 'ClientPostDeleteConfirmation':
          case 'ClientReplyToCommentEvent':
          case 'ClientCommentOnPostEvent':
          case 'ClientPollExpiredEvent': {
            const postId = getRelatedEntityId('postId', data);
            if (!postId) {
              console.warn(`Failed to get post from ${data.eventName}`);
              return;
            }
            queryClient.invalidateQueries({
              queryKey: [API_CACHE_KEYS.POST, { postId }]
            });
            queryClient.invalidateQueries({
              queryKey: [API_CACHE_KEYS.COMMENTS, postId]
            });
            return;
          }
          case 'ClientOrganizationMemberAddedEvent': {
            const organizationId = getRelatedEntityId('organizationId', data);
            if (!organizationId) {
              console.warn(
                'Failed to get organization from ClientOrganizationMemberAddedEvent'
              );
              return;
            }
            queryClient.invalidateQueries({
              queryKey: [API_CACHE_KEYS.ORGANIZATION, organizationId]
            });
            queryClient.invalidateQueries({
              queryKey: [API_CACHE_KEYS.ORGANIZATION_MEMBERS, organizationId]
            });
            queryClient.invalidateQueries({
              queryKey: [API_CACHE_KEYS.OWN_ORGANIZATIONS]
            });
            return;
          }
          case 'ClientGroupMembershipStatusChanged':
          case 'ClientGroupPendingJoinAcceptConfirmation':
          case 'ClientGroupPendingJoinDenyConfirmation': {
            const groupId = getRelatedEntityId('groupId', data);
            if (!groupId) {
              console.warn(`Failed to get group from ${data.eventName}`);
              return;
            }
            queryClient.invalidateQueries({
              queryKey: [API_CACHE_KEYS.GROUP_MEMBERS, groupId]
            });
            queryClient.invalidateQueries({
              queryKey: [API_CACHE_KEYS.GROUPS, groupId]
            });
            return;
          }
          case 'ClientChatMessageReceivedEvent':
          case 'ClientChatMessageDeletedEvent': {
            const chatId = getRelatedEntityId('chatId', data);
            if (!chatId) {
              console.warn(`Failed to get chat from ${data.eventName}`);
              return;
            }
            queryClient.invalidateQueries({
              queryKey: [API_CACHE_KEYS.CHAT, chatId]
            });
            queryClient.invalidateQueries({
              queryKey: [API_CACHE_KEYS.CHAT_MESSAGES, chatId]
            });
            await refetchOwnChats(); // ensure fetch completes and badge updates
            return;
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      queryClient,
      refetchOwnChats,
      theme.colors.error,
      updateBadge,
      updateNewPostBadgeEntriesChanged
    ]
  );

  defineTask<BackgroundNotification>(
    BACKGROUND_NOTIFICATION_TASK,
    // oxlint-disable-next-line @typescript-eslint/require-await
    async ({ data, error }) => {
      updateDebugMessage(JSON.stringify(data, undefined, 2));
      console.info(
        `${Platform.OS} BACKGROUND-NOTIFICATION-TASK: App in ${AppState.currentState} state.`
      );
      const isNotificationResponse = !isEmptyOrNull(data.actionIdentifier);

      if (error) console.error(error);

      if (isNotificationResponse) {
        // Do something with the notification response from user
        console.info('isNotificationResponse');
      } else {
        // Do something with the data from notification that was received
        const notificationData = getBackgroundNotificationData(data);
        handleNotification(notificationData);
      }
    }
  );

  // Set OS token and initialize listeners
  useEffect(() => {
    if (isWeb) return;

    registerTaskAsync(BACKGROUND_NOTIFICATION_TASK)
      .then(() => console.info('REGISTERED: BACKGROUND_NOTIFICATION_TASK'))
      .catch((_error) => console.error(_error));

    responseListener.current = addNotificationResponseReceivedListener(
      async () => {
        await setBadgeCountAsync(0);
      }
    );

    setBadgeCountAsync(0);

    return () => {
      if (responseListener.current) {
        responseListener.current.remove();
      }
    };
  }, []);

  // Backend register/unregister (handles logout and user switches)
  useEffect(() => {
    if (isWeb) {
      return;
    }

    const run = async () => {
      try {
        if (!pushToken) {
          console.warn('Push token is not available');
          return;
        }

        if (isAuthenticated && userId) {
          // If previously registered to a different user, unregister first
          if (personId && personId !== userId) {
            console.info('Unregister mobile push (User changed)');
            await unRegisterMobilePush.mutateAsync({
              token: pushToken,
              personId: personId,
              platform: PUSH_PLATFORM
            });
            setPersonId(undefined);
            updateToken();
          }
          // Register if not already registered for this user
          if (personId !== userId && isAuthenticated) {
            console.info('Register mobile push');
            await registerMobilePush.mutateAsync({
              token: pushToken,
              platform: PUSH_PLATFORM
            });
            setPersonId(userId);
          }
        } else {
          // Unregister on logout or loss of userId
          if (personId) {
            console.info('Unregister mobile push (Logout)');
            await unRegisterMobilePush.mutateAsync({
              token: pushToken,
              personId: personId,
              platform: PUSH_PLATFORM
            });
            setPersonId(undefined);
            updateToken();
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    void run();
  }, [
    isAuthenticated,
    userId,
    pushToken,
    personId,
    registerMobilePush,
    unRegisterMobilePush,
    updateToken
  ]);

  // oxlint-disable-next-line unicorn/no-null
  return null;
};
